import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ProductSupplierQuoteService } from '@app/api/productSupplierQuote.service';
import { ProductService } from '@app/api/product.service';
import {
  ProductSupplierQuote,
  CreateSupplierQuotePayload,
  UpdateSupplierQuotePayload,
  formatQuantity
} from '@app/model/productSupplierQuote';

import { TranslateService } from '@ngx-translate/core';

export interface ModalSupplierQuoteData {
  productId: string;
  productName?: string;
  variants?: Array<{ id: string; name: string }>;
  quote?: ProductSupplierQuote;
}

@Component({
  selector: 'meu-modal-supplier-quote',
  templateUrl: './modal-supplier-quote.component.html',
  styleUrls: ['./modal-supplier-quote.component.scss']
})
export class ModalSupplierQuoteComponent implements OnInit {
  form: FormGroup;
  isEdit = false;
  submitting = false;
  loadingVariants = false;
  variants: Array<{ id: string; name: string }> = [];

  constructor(
    public dialogRef: MatDialogRef<ModalSupplierQuoteComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ModalSupplierQuoteData,
    private fb: FormBuilder,
    private quoteService: ProductSupplierQuoteService,
    private productService: ProductService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    this.isEdit = !!data.quote;
    this.variants = data.variants || [];
  }

  ngOnInit(): void {
    const q = this.data.quote;
    // Format quote_time for datetime-local (YYYY-MM-DDTHH:mm)
    let defaultTime = '';
    if (q && q.quote_time) {
      const d = new Date(q.quote_time);
      defaultTime = this.formatDateTimeLocal(d);
    } else {
      defaultTime = this.formatDateTimeLocal(new Date());
    }

    this.form = this.fb.group({
      supplier_name: [
        q ? q.supplier_name : '',
        [Validators.required, Validators.maxLength(200)]
      ],
      product_type_id: [q ? q.product_type_id : null],
      price: [
        q ? q.price : null,
        [Validators.required, Validators.min(0)]
      ],
      quantity: [
        q ? q.quantity : null,
        [Validators.required, Validators.min(0)]
      ],
      quote_time: [defaultTime, [Validators.required]],
      note: [q ? q.note : '', [Validators.maxLength(200)]]
    });

    if ((!this.variants || this.variants.length === 0) && this.data.productId) {
      this.loadVariants();
    }
  }

  loadVariants(): void {
    if (!this.data.productId) return;
    this.loadingVariants = true;
    this.productService.apiProductsIdGet(this.data.productId).subscribe(
      (res) => {
        this.loadingVariants = false;
        if (res && res.success && res.data) {
          const list: Array<{ id: string; name: string }> = [];
          const rawTypes = res.data.extend_product_type || res.data.type_values || [];
          if (Array.isArray(rawTypes) && rawTypes.length > 0) {
            const typeLookup = new Map<string, string>();
            if (res.data.extend_type && Array.isArray(res.data.extend_type)) {
              res.data.extend_type.forEach((group: any) => {
                if (group && group.types && Array.isArray(group.types)) {
                  group.types.forEach((t: any) => {
                    if (t && t.id) {
                      const label = group.group_name ? `${group.group_name}: ${t.name}` : t.name;
                      typeLookup.set(t.id, label);
                    }
                  });
                }
              });
            }

            rawTypes.forEach((pt: any) => {
              if (!pt || !pt.id) return;
              let displayName = pt.name || pt.product_type_text || '';
              if (!displayName && pt.types && Array.isArray(pt.types)) {
                displayName = pt.types
                  .map((t: any) => t.name)
                  .filter((n: any) => !!n)
                  .join(' - ');
              }
              if (!displayName && pt.type_id && Array.isArray(pt.type_id)) {
                const names = pt.type_id
                  .map((tid: string) => typeLookup.get(tid))
                  .filter((n: any) => !!n);
                if (names.length > 0) {
                  displayName = names.join(' - ');
                }
              }
              if (!displayName) {
                displayName = this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.VARIANT_FALLBACK', {
                  index: list.length + 1
                });
              }
              list.push({ id: pt.id, name: displayName });
            });
          }
          this.variants = list;
        }
      },
      (err) => {
        this.loadingVariants = false;
        console.error('[ModalSupplierQuote] loadVariants error', err);
      }
    );
  }

  private formatDateTimeLocal(d: Date): string {
    if (!d || isNaN(d.getTime())) return '';
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const val = this.form.value;
    const selectedVariant = this.variants.find((v) => v.id === val.product_type_id);
    const productTypeText = selectedVariant ? selectedVariant.name : null;

    // Convert datetime-local to ISO string
    const quoteTimeIso = val.quote_time
      ? new Date(val.quote_time).toISOString()
      : new Date().toISOString();

    this.submitting = true;

    if (this.isEdit && this.data.quote) {
      const updatePayload: UpdateSupplierQuotePayload = {
        product_type_id: val.product_type_id || null,
        product_type_text: productTypeText,
        supplier_name: val.supplier_name.trim(),
        price: Number(val.price),
        quantity: Number(val.quantity),
        quote_time: quoteTimeIso,
        note: val.note ? val.note.trim() : null
      };

      this.quoteService.updateSupplierQuote(this.data.quote.id, updatePayload).subscribe(
        (res) => {
          this.submitting = false;
          if (res && res.success) {
            this.toastr.success(res.message || this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.UPDATE_SUCCESS'));
            this.dialogRef.close({ saved: true, data: res.data });
          } else {
            this.toastr.error(res && res.message ? res.message : this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.UPDATE_ERROR'));
          }
        },
        (err) => {
          this.submitting = false;
          console.error('[ModalSupplierQuote] update error', err);
          const msg = (err && err.error && err.error.message) || this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.UPDATE_ERROR');
          this.toastr.error(msg);
        }
      );
    } else {
      const createPayload: CreateSupplierQuotePayload = {
        product_id: this.data.productId,
        product_type_id: val.product_type_id || null,
        product_type_text: productTypeText,
        supplier_name: val.supplier_name.trim(),
        price: Number(val.price),
        quantity: Number(val.quantity),
        quote_time: quoteTimeIso,
        note: val.note ? val.note.trim() : null
      };

      this.quoteService.createSupplierQuote(createPayload).subscribe(
        (res) => {
          this.submitting = false;
          if (res && res.success) {
            this.toastr.success(res.message || this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.CREATE_SUCCESS'));
            this.dialogRef.close({ saved: true, data: res.data });
          } else {
            this.toastr.error(res && res.message ? res.message : this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.CREATE_ERROR'));
          }
        },
        (err) => {
          this.submitting = false;
          console.error('[ModalSupplierQuote] create error', err);
          const msg = (err && err.error && err.error.message) || this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.CREATE_ERROR');
          this.toastr.error(msg);
        }
      );
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
